// src/pages/WelcomePage.js
import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Clock, PieChart, Sparkles, ListChecks, Smile, Cloud, Zap,
  BookOpenText, Info, User, Mail, Languages
} from "lucide-react";
import KeyConceptQuote from "../components/KeyConceptQuote";
import BookSummaryTooltip from "../components/BookSummaryTooltip";
import { Helmet } from 'react-helmet-async';

// Screenshot images (path ต้องถูกกับ public/)
const screenshots = [
  "/IMG_8047.png",
  "/IMG_8048.png",
  "/IMG_8049.png",
  "/IMG_8050.png",
  "/IMG_8051.png",
  "/IMG_8052.png",
  "/IMG_8053.png",
  "/IMG_8054.png",
  "/IMG_8055.png",
  "/IMG_8056.png",
  "/IMG_8057.png",
  "/IMG_8058.png",
  "/IMG_8059.png",
  "/IMG_8060.png",
  "/IMG_8061.png",
  "/IMG_8062.png"
];

const SECTIONS = [
  { key: "welcome", label: { th: "หน้าแรก", en: "Home" } },
  { key: "features", label: { th: "ฟีเจอร์", en: "Features" } },
  { key: "screenshots", label: { th: "หน้าจอตัวอย่าง", en: "Screenshots" } },
  { key: "story", label: { th: "เรื่องราว", en: "Story" } },
  { key: "contact", label: { th: "ติดต่อ", en: "Contact" } }
];

// Locale Content
const CONTENT = {
  th: {
    heroTitle: "Spendora",
    heroDesc: (
      <>
        Modern Activity & Time Tracker
        <br />
        <span className="text-base text-neutral-400">
          ใช้ชีวิตให้มีคุณค่ากับแอพที่ดูดี ใช้ง่าย และชวนสะท้อนตัวเอง
        </span>
      </>
    ),
    openApp: "เริ่มใช้งาน / Open App",
    support: "☕ สนับสนุน",
    features: [
      {
        icon: <Clock size={44} className="mb-3 text-indigo-500" />,
        title: "บันทึกและสะท้อน",
        desc: "บันทึกและย้อนดูว่าคุณใช้เวลาแต่ละวันกับอะไร"
      },
      {
        icon: <PieChart size={44} className="mb-3 text-green-500" />,
        title: "อินไซต์ & กราฟ",
        desc: "กราฟ, Infographic เห็นจุดสมดุลชีวิตและเทรนด์"
      },
      {
        icon: <Sparkles size={44} className="mb-3 text-yellow-400" />,
        title: "นิสัย & Streak",
        desc: "ตั้งเป้า, สร้าง streak, ปลุกนิสัยดีในทุกวัน"
      },
      {
        icon: <ListChecks size={44} className="mb-3 text-rose-400" />,
        title: "หมวดหมู่ชีวิตที่คัดสรรแล้ว",
        desc: "เลือกหมวดหมู่กิจกรรมที่ตรงกับทุกมิติชีวิต"
      },
      {
        icon: <Smile size={44} className="mb-3 text-pink-400" />,
        title: "อารมณ์ & สุขภาวะ",
        desc: "เช็คอารมณ์และดูสถิติสุขภาวะจิตใจ"
      },
      {
        icon: <Cloud size={44} className="mb-3 text-sky-400" />,
        title: "ซิงค์ข้อมูลบน Cloud",
        desc: "ข้อมูลปลอดภัย เข้าถึงได้ทุกที่ ทุกอุปกรณ์"
      },
      {
        icon: <Zap size={44} className="mb-3 text-amber-400" />,
        title: "Minimal & Fast",
        desc: "ดีไซน์คลีน ใช้งานไว ไม่หนักเครื่อง"
      }
    ],

    concepts: [
        {
          title: "เริ่มต้นจากการรู้ว่าเวลาหายไปไหน",
          desc: "ในแต่ละวันคนส่วนใหญ่ไม่ค่อยหรืออาจจะไม่เคยย้อนถามว่า เวลาหายไปไหน... แต่การเปลี่ยนแปลงจะเริ่มต้นได้ ก็ต่อเมื่อเรารู้ตัวว่าใช้เวลากับอะไร",
          tip: "เขียนวันละบรรทัด: วันนี้คุณใช้เวลากับอะไร? และคุณรู้สึกอย่างไร?"
        },
        {
          title: "นิสัยเล็ก ๆ สร้างการเปลี่ยนแปลงใหญ่",
          desc: "การเปลี่ยนแปลงที่แท้จริงไม่เคยเริ่มจากสิ่งยิ่งใหญ่ แต่มาจากอะไรเล็ก ๆ ที่ทำได้ซ้ำ ๆ เช่น ตื่นเช้า 10 นาที วางแผนวันพรุ่งนี้ หรือจัดโต๊ะให้เรียบร้อย",
          tip: "เริ่มจากสิ่งเล็ก ๆ แล้วทำให้สม่ำเสมอ ดีกว่าทำครั้งละมาก ๆ แล้วหยุดไป"
        },
        {
          title: "เวลา คือการลงทุนที่แท้จริง",
          desc: "ความสุขไม่ได้มาจากการทำงานหนักหรือเงินเยอะเสมอไป แต่มาจากการใช้เวลากับสิ่งที่เติมเต็มหัวใจ—ครอบครัว ตัวเราเอง ความสงบ",
          tip: "จัดเวลาให้กับสิ่งที่เยียวยาใจคุณ เช่น ออกกำลังกาย เล่นกับลูก หรือหัวเราะกับคนที่รัก"
        },
        {
          title: "อย่าแข่งขันกับใคร ให้เมตตากับตัวเอง",
          desc: "คุณไม่จำเป็นต้องแข่งกับใคร ชีวิตคือการเดินทางเทียบกับ 'ตัวคุณเมื่อวานนี้' ต่างหาก และในวันที่ยากลำบาก... แค่เริ่มใหม่อย่างอ่อนโยนก็พอ",
          tip: "การล้มไม่ใช่ความพ่ายแพ้ สิ่งสำคัญคือการลุกขึ้นอีกครั้ง…ด้วยความเข้าใจ"
        },
        {
          title: "การทบทวน คือเส้นทางสู่การเติบโต",
          desc: "บางครั้ง การเติบโตไม่ได้มาจากการทำมากขึ้น แต่มาจากการมองเห็นชัดขึ้น ลองกลับมาทบทวนว่าอะไรเติมพลัง อะไรพรากพลัง และคุณอยากปรับอะไร",
          tip: "ทุกสัปดาห์ ลองเขียน 2 สิ่ง: สิ่งที่ภูมิใจ และสิ่งที่อยากปรับปรุง"
        },
        {
          title: "ใช้เวลาให้มีคุณค่า…เหมือนที่คุณใส่ใจกับเงินทุกบาท",
          desc: "เราใช้เวลาโดยไม่รู้ตัว แต่จัดการเงินด้วยความระวัง ลองเปลี่ยน—ให้คุณใช้เวลาอย่างมีคุณค่า เหมือนกับการใช้จ่ายเงินในสิ่งที่สำคัญจริง ๆ",
          tip: "ลองตั้งเวลา 'No Phone Zone' หรือช่วงเวลาสำหรับงานลึก (Deep Work)"
        }
    ],

    screenshots: "หน้าจอตัวอย่าง",
    story: "เรื่องราวของ Spendora",
    storyContent: (
      <>
        <p>
          Spendora สร้างขึ้นโดยนักพัฒนา/{" "}
          <a
            href="https://www.facebook.com/people/%E0%B9%80%E0%B8%AD%E0%B9%8B%E0%B8%AA%E0%B8%B8%E0%B8%94%E0%B8%AA%E0%B8%B1%E0%B8%9B%E0%B8%94%E0%B8%B2%E0%B8%AB%E0%B9%8C/61577047885891"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline hover:text-blue-800 transition"
          >
            นักเขียน(เอ๋สุดสัปดาห์)
          </a>
          /{" "}
          <a
            href="https://www.readawrite.com/?action=user_page&user_id_publisher=12399010"
            target="_blank"
            rel="noopener noreferrer"
            className="text-yellow-600 underline hover:text-yellow-800 transition"
          >
            ReadAWrite
          </a>
          {" "}ผู้เชื่อว่า <b>เวลา</b> คือทรัพยากรที่มีค่าที่สุดในชีวิต
        </p>
        <p className="mt-4">
          จุดเริ่มต้นของ Spendora มาจากหนังสือ <b>時間もお金も増える習慣 (Jikan mo Okane mo Fueru Shūkan)</b> ภาษาไทยว่า "อุปนิสัยที่ช่วยเพิ่มทั้งเวลาและเงินทอง" ที่เปลี่ยนทัศนคติกับ "เป้าหมายชีวิต" อย่างลึกซึ้ง
        </p>
        <div className="text-center">
          <BookSummaryTooltip />
        </div>
        <p>
          หนังสือเล่มนี้ทำให้เข้าใจว่า <span className="text-indigo-600 font-semibold">"การจัดการเวลาที่ดีสำคัญยิ่งกว่าการหาเงิน"</span> คนส่วนใหญ่ไล่ตามเงินจนลืมดูว่าเวลาหมดไปกับอะไร ใช้ชีวิตกับคนที่รักน้อยแค่ไหน หรือดูแลตัวเองน้อยแค่ไหน
        </p>
        <p className="mt-4">
          Spendora จึงเกิดขึ้นเพื่อ "สะท้อน" <span className="text-indigo-600 font-semibold">คุณค่าของแต่ละวัน</span> ให้เห็นภาพชัด ช่วยคุณจัดสรรเวลา สร้างนิสัยดี ๆ และมีความสมดุลทั้งงาน ชีวิต ครอบครัว สุขภาพ
        </p>
        <p className="mt-4 italic text-gray-500">
          ทำไมชื่อ Spendora?
          <br />
          <b>Spendora</b> มาจาก <span className="text-indigo-600 font-semibold">"Spend" + "Aurora"</span> (การใช้จ่าย) + (แสงสว่างแห่งเวลา) = หมายถึง <span className="text-indigo-600 font-semibold">"แสงสว่างที่เกิดจากการใช้เวลาอย่างมีคุณค่า"</span>
        </p>
      </>
    ),
    contact: "ติดต่อ",
    contactName: "Spendora",
    copyright: () => <>© {new Date().getFullYear()} Spendora</>,
  },
  en: {
    heroTitle: "Spendora",
    heroDesc: (
      <>
        Modern Activity & Time Tracker
        <br />
        <span className="text-base text-neutral-400">
          Make your days more meaningful, one habit at a time.
        </span>
      </>
    ),
    openApp: "Open App",
    support: "☕ Support",
    features: [
      {
        icon: <Clock size={44} className="mb-3 text-indigo-500" />,
        title: "Track & Reflect",
        desc: "Log your activities, manage your time, and see your growth."
      },
      {
        icon: <PieChart size={44} className="mb-3 text-green-500" />,
        title: "Insightful Analytics",
        desc: "View clear infographics and trends for better balance."
      },
      {
        icon: <Sparkles size={44} className="mb-3 text-yellow-400" />,
        title: "Growth Mindset",
        desc: "Set goals, track streaks, and build better habits."
      },
      {
        icon: <ListChecks size={44} className="mb-3 text-rose-400" />,
        title: "Curated Categories",
        desc: "Pick from handpicked activity categories for better life insights."
      },
      {
        icon: <Smile size={44} className="mb-3 text-pink-400" />,
        title: "Mood & Wellbeing Log",
        desc: "Check your mood and see mental health stats."
      },
      {
        icon: <Cloud size={44} className="mb-3 text-sky-400" />,
        title: "Cloud Sync & Data Safety",
        desc: "Your data is safe and accessible everywhere."
      },
      {
        icon: <Zap size={44} className="mb-3 text-amber-400" />,
        title: "Minimal & Fast",
        desc: "Clean design, fast performance, zero clutter."
      }
    ],

    concepts: [
        {
          title: "Become aware of your time",
          desc: "Most people never stop to think where their hours go. But change begins with awareness. When you track your day, you begin to see yourself.",
          tip: "Write one line a day: What did you spend time on? And how did it make you feel?",
          icon: <Clock className="text-indigo-500" size={32} />
        },
        {
          title: "Small habits, big change",
          desc: "Great transformation never begins with leaps. It starts with something small—ten minutes earlier, a clear desk, or a quiet plan for tomorrow.",
          tip: "Start small. Be consistent. That's more powerful than being intense.",
          icon: <ListChecks className="text-rose-500" size={32} />
        },
        {
          title: "Time is the real investment",
          desc: "Happiness doesn't come from more work or more money. It comes from spending your time where your heart truly is—family, self, peace.",
          tip: "Block time for what restores you—exercise, hobbies, laughter with loved ones.",
          icon: <PieChart className="text-green-500" size={32} />
        },
        {
          title: "Grace for yourself, not competition",
          desc: "You don't have to keep up with anyone. The real journey is with your past self. And on hard days? Begin again with kindness.",
          tip: "No shame in falling. Just rise gently.",
          icon: <Smile className="text-pink-400" size={32} />
        },
        {
          title: "Reflection brings growth",
          desc: "Growth isn't just doing more. It's seeing clearly. Reflect weekly on what nourished you, what drained you, and what you want to change.",
          tip: "Each Sunday, write two things: What you're proud of, and what to improve.",
          icon: <BookOpenText className="text-yellow-500" size={32} />
        },
        {
          title: "Spend time like you spend money",
          desc: "We budget money. But time? We waste it. Learn to guard your focus and spend your hours like treasures.",
          tip: "Try a 'No Phone Zone' or schedule deep focus hours.",
          icon: <Zap className="text-amber-400" size={32} />
        }
      ],

    screenshots: "Screenshots",
    story: "The Story Behind Spendora",
    storyContent: (
      <>
        <p>
          Spendora was built by a developer and{" "}
          <a
            href="https://www.facebook.com/people/%E0%B9%80%E0%B8%AD%E0%B9%8B%E0%B8%AA%E0%B8%B8%E0%B8%94%E0%B8%AA%E0%B8%B1%E0%B8%9B%E0%B8%94%E0%B8%B2%E0%B8%AB%E0%B9%8C/61577047885891"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline hover:text-blue-800 transition"
          >
            writer
          </a>
          {" "}who deeply believes in the power of <b>time</b> — and wanted to create a tool that makes each day feel more intentional and meaningful.
        </p>
        <p className="mt-4">
          The inspiration came from a book called <b>時間もお金も増える習慣 (Jikan mo Okane mo Fueru Shūkan)</b>, which deeply shifted the perspective on what truly matters in life.
          <br /><br />
          Its core insight: <span className="text-indigo-600 font-semibold">managing time well matters more than chasing money.</span>{" "}
          Most people track every dollar but let their hours slip away unnoticed — away from loved ones, away from themselves.
        </p>
        <p className="mt-4">
          Spendora was born to change that — to reflect the true value of each day, and help you organize your time, build better habits, and find a healthier balance between work, life, family, and health.
        </p>
        <p className="mt-4 italic text-gray-500">
          <b>Why the name Spendora?</b>
          <br />
          Spendora comes from <span className="text-indigo-600 font-semibold">"Spend" + "Aurora"</span>, the light of time. It represents <span className="text-indigo-600 font-semibold">"the radiance that comes from spending your time meaningfully."</span>
        </p>
      </>
    ),
    contact: "Contact",
    contactName: "Spendora",
    copyright: () => <>© {new Date().getFullYear()} Spendora</>,
  }
};

export default function WelcomePage() {
  const [lang, setLang] = useState("th"); // Default lang
  const t = CONTENT[lang];

  // Section scroll spy
  const sectionRefs = useRef({
    welcome: null,
    features: null,
    screenshots: null,
    story: null,
    contact: null
  });
  const [activeSection, setActiveSection] = useState("welcome");
  

  // Screenshot carousel state
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setCurrent(c => (c + 1) % screenshots.length), 3500);
    return () => clearInterval(timer);
  }, []);

  // Scroll spy logic
  useEffect(() => {
    const refs = sectionRefs.current;
    const handleScrollSpy = entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) setActiveSection(entry.target.id);
      });
    };
    const observer = new window.IntersectionObserver(handleScrollSpy, {
      rootMargin: "-40% 0px -50% 0px",
      threshold: 0.1
    });
    Object.values(refs).forEach(ref => {
      if (ref) observer.observe(ref);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToSection = key => {
    sectionRefs.current[key]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
    <Helmet>
      <title>{lang === "th" ? "Spendora – บันทึกเวลาและชีวิตอย่างมีคุณค่า" : "Spendora – Time Tracker & Life Reflection App"}</title>
      <meta
        name="description"
        content={
          lang === "th"
            ? "Spendora ช่วยคุณสะท้อนคุณค่าของเวลา สร้างนิสัยดี และมีชีวิตสมดุลผ่านการบันทึกกิจกรรมและกราฟอินไซต์"
            : "Spendora helps you reflect on time, build better habits, and live more intentionally with daily tracking and insights."
        }
      />
      <meta property="og:title" content="Spendora – Time Tracker for Meaningful Living" />
      <meta
        property="og:description"
        content="Track your day, reflect deeply, and see what matters most. Spendora helps you live with clarity."
      />
      <meta property="og:image" content="https://spendora.thatwhy.me/og-image.png" />
      <meta property="og:url" content="https://spendora.thatwhy.me/welcome" />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
    </Helmet>
    <div className="min-h-screen bg-gradient-to-br from-neutral-100 to-white flex flex-col relative">
      {/* Sticky Navigator */}
      <nav className="sticky top-0 left-0 z-20 bg-white/80 backdrop-blur border-b border-gray-100 shadow-sm">
        <div className="max-w-6xl mx-auto flex flex-wrap md:flex-nowrap items-center justify-between px-4 py-3 gap-y-2">
            {/* Logo */}
            <div className="flex items-center gap-3">
            <img src="/logo512.png" alt="Spendora" className="h-8 w-8 rounded-xl shadow" />
            <span className="text-lg font-black tracking-tight">Spendora</span>
            </div>

            {/* Desktop nav buttons */}
            <div className="hidden md:flex gap-1 flex-1 justify-center">
            {SECTIONS.map(({ key, label }) => (
                <button
                key={key}
                onClick={() => scrollToSection(key)}
                className={
                    "px-4 py-2 rounded-full font-medium transition-all duration-150 " +
                    (activeSection === key
                    ? "bg-indigo-600 text-white shadow"
                    : "text-gray-700 hover:bg-indigo-50")
                }
                >
                {label[lang]}
                </button>
            ))}
            </div>

            {/* Right tools */}
            <div className="flex items-center gap-2">
            <button
                onClick={() => setLang(lang === "en" ? "th" : "en")}
                className="flex gap-2 items-center px-3 py-2 border border-gray-200 rounded-xl bg-white text-gray-800 font-semibold hover:bg-indigo-50 transition"
            >
                <Languages size={18} />
                {lang === "en" ? "TH" : "EN"}
            </button>
            <Link
                to="/"
                className="px-4 py-2 rounded-full bg-black text-white font-semibold shadow hover:bg-neutral-900 transition"
            >
                {t.openApp}
            </Link>
            </div>
        </div>

        {/* Mobile nav (if needed) */}
        <div className="md:hidden px-4 pb-2 flex overflow-x-auto gap-2">
            {SECTIONS.map(({ key, label }) => (
            <button
                key={key}
                onClick={() => scrollToSection(key)}
                className={
                "px-3 py-1 rounded-full text-sm whitespace-nowrap " +
                (activeSection === key
                    ? "bg-indigo-600 text-white shadow"
                    : "text-gray-600 hover:bg-gray-100")
                }
            >
                {label[lang]}
            </button>
            ))}
        </div>
      </nav>

      {/* Hero/Welcome Section */}
      <section
        ref={el => (sectionRefs.current.welcome = el)}
        id="welcome"
        className="pt-2 pb-2 flex flex-col items-center min-h-[75vh]"
      >
        <img
          src="/Logo.gif"
          alt="Spendora Logo"
          className="w-28 h-28 mb-7 rounded-3xl shadow-xl border border-gray-200 bg-white"
        />
        <h1 className="text-5xl font-black text-neutral-900 mb-3 tracking-tight leading-tight">{t.heroTitle}</h1>
        <p className="text-xl text-gray-500 mb-6 max-w-xl text-center font-light">{t.heroDesc}</p>
        <div className="flex gap-4 mt-2">
          <Link
            to="/"
            className="px-7 py-3 bg-black text-white rounded-2xl font-semibold text-lg shadow hover:bg-neutral-800 transition-all duration-150"
          >
            {t.openApp}
          </Link>
          <a
            href="https://buymeacoffee.com/spendora?status=1"
            target="_blank"
            rel="noopener noreferrer"
            className="px-7 py-3 bg-white text-black border border-gray-300 rounded-2xl font-semibold text-lg shadow hover:bg-neutral-100 transition-all duration-150"
          >
            {t.support}
          </a>
        </div>
        <br></br>
        <br></br>
        <KeyConceptQuote lang={lang} />
      </section>

      

      {/* Features Section */}
      <section
        ref={el => (sectionRefs.current.features = el)}
        id="features"
        className="flex-1 max-w-5xl mx-auto px-4 mb-16"
      >
        <h2 className="text-2xl font-semibold text-neutral-900 mb-7 text-center">{SECTIONS[1].label[lang]}</h2>
        <div className="grid md:grid-cols-3 gap-7">
          {t.features.map(({ icon, title, desc }, idx) => (
            <div key={title} className="rounded-2xl bg-white shadow-lg p-8 flex flex-col items-center hover:scale-[1.04] transition">
              {icon}
              <h3 className="font-bold text-lg mb-1 text-neutral-800">{title}</h3>
              <p className="text-sm text-gray-500 font-light text-center">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Screenshots Section (Carousel) */}
      <section
        ref={el => (sectionRefs.current.screenshots = el)}
        id="screenshots"
        className="py-14 px-4 max-w-5xl mx-auto flex flex-col items-center"
      >
        <h2 className="text-2xl font-semibold text-neutral-900 mb-4 text-center">
          <BookOpenText className="inline-block mr-2 -mt-1" size={26} /> {t.screenshots}
        </h2>
        <div className="w-full flex flex-col items-center mb-6">
          <div className="relative w-full max-w-xl aspect-[16/10] rounded-3xl overflow-hidden shadow-2xl border border-gray-200 bg-white">
            {screenshots.map((src, idx) => (
              <img
                key={src}
                src={src}
                alt={`Screenshot ${idx + 1}`}
                style={{
                  opacity: idx === current ? 1 : 0,
                  transition: "opacity 0.7s",
                  position: "absolute",
                  top: 0, left: 0, width: "100%", height: "100%", objectFit: "contain"
                }}
                loading="lazy"
              />
            ))}
          </div>
          {/* Manual nav dots */}
          <div className="flex gap-2 mt-4">
            {screenshots.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrent(idx)}
                className={`w-3 h-3 rounded-full transition-all duration-150 border ${
                  idx === current ? "bg-indigo-500 border-indigo-500 scale-110" : "bg-gray-200 border-gray-300"
                }`}
                aria-label={`Go to screenshot ${idx + 1}`}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-wrap justify-center gap-4 mt-4">
          {screenshots.map((src, idx) => (
            <img
              key={src}
              src={src}
              alt={`Thumbnail ${idx + 1}`}
              className="rounded-xl shadow w-28 border border-gray-200 object-cover cursor-pointer transition-all hover:scale-105"
              style={{
                opacity: idx === current ? 0.4 : 1,
                filter: idx === current ? "grayscale(60%) blur(1px)" : "none"
              }}
              loading="lazy"
              onClick={() => setCurrent(idx)}
            />
          ))}
        </div>
      </section>

      {/* Story/About Section */}
      <section
        ref={el => (sectionRefs.current.story = el)}
        id="story"
        className="py-14 max-w-3xl mx-auto px-4"
      >
        <h2 className="text-2xl font-semibold text-neutral-900 mb-5 flex items-center gap-2">
          <Info size={24} /> {t.story}
        </h2>
        <div className="flex flex-col md:flex-row items-start gap-8 mb-4">
          <img
            src="/avatar.JPG"
            alt="Creator"
            className="w-36 h-36 rounded-full border-4 border-indigo-100 shadow-lg object-cover bg-white"
            style={{ objectPosition: "center" }}
          />
          <div className="flex-1 text-gray-700 text-lg font-light leading-relaxed">
            {t.storyContent}
          </div>
        </div>
      </section>

      {/* Key Concepts Section */}
      <section className="py-14 max-w-5xl mx-auto px-4">
        <h2 className="text-2xl font-semibold text-neutral-900 mb-7 text-center">
            💡 {lang === "th" ? "แนวคิดเบื้องหลัง Spendora" : "Key Concepts Behind Spendora"}
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
            {(t.concepts || []).map(({ title, desc, tip, icon }, idx) => (
            <div
                key={idx}
                className="bg-white shadow-md p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition"
            >
                <div className="flex items-start gap-4 mb-2">
                <div>{icon}</div>
                <div>
                    <h3 className="font-semibold text-gray-800 text-lg">{title}</h3>
                    <p className="text-sm text-gray-600 mt-1 leading-relaxed">{desc}</p>
                </div>
                </div>
                <div className="text-xs text-gray-500 mt-3 italic">☕ Tip: {tip}</div>
            </div>
            ))}
        </div>
      </section>

      {/* Contact/Footer */}
      <section
        ref={el => (sectionRefs.current.contact = el)}
        id="contact"
        className="py-10"
      >
        <footer className="text-center text-gray-400 text-base border-t border-gray-100 pt-8">
          <div className="flex items-center justify-center gap-4 mb-2">
            <User size={20} /> {t.contactName}
            <Mail size={20} className="ml-5" />
            <a href="mailto:thungong@thatwhy.me" className="underline hover:text-blue-600">thungong@thatwhy.me</a>
          </div>
          <div className="mt-2">{t.copyright()}</div>
        </footer>
      </section>
    </div>
    </>
  );
}